import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Mail, Save, Power } from "lucide-react";

const AdminNotifications = () => {
  const { t } = useLang();
  const [slots, setSlots] = useState([
    { email: "", enabled: true },
    { email: "", enabled: true },
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/settings/notifications")
      .then((r) => {
        const s = (r.data?.slots || []).slice(0, 2);
        while (s.length < 2) s.push({ email: "", enabled: false });
        setSlots(s);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const update = (i, patch) => {
    setSlots((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/settings/notifications", { slots });
      const s = (res.data?.slots || []).slice(0, 2);
      while (s.length < 2) s.push({ email: "", enabled: false });
      setSlots(s);
      toast.success(t("admin.notifsSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-[#736B66] dark:text-[#B3B3B3]">{t("admin.loading") || "..."}</div>;
  }

  return (
    <div className="max-w-2xl" data-testid="admin-notifications-panel">
      <div className="bg-white dark:bg-[#212121] border border-[#EBE5DF] dark:border-[#404040] p-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-[#D4AF37]/10 text-[#7A6017] dark:text-[#D4AF37] rounded">
            <Mail size={22} />
          </div>
          <div className="flex-1">
            <h3 className="font-serif text-2xl mb-1 text-[#2C1E16] dark:text-[#FFFFFF]">
              {t("admin.notifsTitle")}
            </h3>
            <p className="text-sm text-[#736B66] dark:text-[#B3B3B3]">
              {t("admin.notifsDesc")}
            </p>
          </div>
        </div>

        <div className="space-y-5">
          {slots.map((slot, i) => (
            <div
              key={i}
              className="border border-[#EBE5DF] dark:border-[#404040] p-5"
              data-testid={`notif-slot-${i}`}
            >
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs tracking-widest uppercase font-semibold text-[#2C1E16] dark:text-[#FFFFFF]">
                  {t("admin.notifSlot")} #{i + 1}
                </label>
                <button
                  type="button"
                  onClick={() => update(i, { enabled: !slot.enabled })}
                  className={`flex items-center gap-2 text-xs tracking-widest uppercase font-semibold px-3 py-1 rounded transition-colors ${
                    slot.enabled
                      ? "bg-[#D4AF37]/15 text-[#7A6017] dark:text-[#D4AF37]"
                      : "bg-[#8B0000]/10 text-[#8B0000]"
                  }`}
                  data-testid={`notif-toggle-${i}`}
                >
                  <Power size={14} />
                  {slot.enabled ? t("admin.notifEnabled") : t("admin.notifDisabled")}
                </button>
              </div>
              <input
                type="email"
                value={slot.email}
                onChange={(e) => update(i, { email: e.target.value })}
                placeholder="admin@amazypet.gr"
                className="luxury-input-box w-full"
                data-testid={`notif-email-${i}`}
              />
              <p className="text-xs text-[#736B66] dark:text-[#B3B3B3] mt-2">
                {slot.enabled
                  ? t("admin.notifWillReceive")
                  : t("admin.notifWontReceive")}
              </p>
            </div>
          ))}
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="luxury-btn mt-6"
          data-testid="notifs-save-btn"
        >
          <Save size={16} />
          {saving ? "..." : t("admin.save")}
        </button>
      </div>
    </div>
  );
};

export default AdminNotifications;
